import { action, extendObservable, reaction } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import once from 'lodash/once';
import _pick from 'lodash/pick';
import { apiCall } from '../API';
import debug from '../debug';
import { REV_TYPE, RULE_REC_TYPE } from '../constants';
import { objectDiff } from '../diff';
import AsyncActionStore from './AsyncActionStore';
import { getDefaultBrowserBarOptions } from './BrowserBarOptions';
import InputStore from './InputStore';
import MenuStore from './MenuStore';
import PaginationStore from './PaginationStore';
import { fetchDisplayRoleName, ROLE_FULL_ACCESS, ROLE_READ_ONLY } from './Administrators/AdministratorModalStore';
import BrowsingRolesStore from './Policy/BrowsingRoles/BrowsingRolesStore';
import ContentRulesStore from './Policy/ContentRules/ContentRulesStore';
import PageRestrictionsStore from './Policy/ContentRules/PageRestrictionsStore';
import CustomCategoriesStore from './Policy/CustomCategories/CustomCategoriesStore';
import ExclusionRulesStore from './Policy/ExclusionRules/ExclusionRulesStore';
import DlpRulesStore from './Policy/FileRules/DlpRules/DlpRulesStore';
import FileThreatRulesStore from './Policy/FileRules/FileThreatRules/FileThreatRulesStore';
import RedirectRulesStore from './Policy/RedirectRules/RedirectRulesStore';
import { getGmtDateString } from './Policy/AccessCodes/AccessCodeInfoStore';

function idMaker() {
    let id = -1;
    return () => {
        id += 1;
        return id;
    };
}

export const revisionType = {
    add: 0,
    mod: 1,
    del: 2,
};

const apiKeyLabelByRevType = {
    [revisionType.add]: 'Add API Key',
    [revisionType.mod]: 'Edit API Key',
    [revisionType.del]: 'Delete API Key',
};
const customerPolicyLabelByRevType = {
    [revisionType.add]: 'Add Policy',
    [revisionType.mod]: 'Edit Policy',
    [revisionType.del]: 'Delete Policy',
};

const accessCodeActions = [
    'CREATE_ACCESS_CODE',
    'EDIT_ACCESS_CODE',
    'DELETE_ACCESS_CODE',
];

/** I have no idea how to make a type in jsdoc that comprises of this object's values.
 * Cbb to learn jsdoc given "why would you ever in your life use jsdoc instead of TS".
 * @typedef PRIORITY_CHANGE_TYPE
 * @type {string} */
export const PRIORITY_CHANGE_TYPES = {
    BROWSING_ROLE: 'Browsing Roles',
    CONTENT_RULE: 'Content Rules',
    REDIRECT_RULE: 'Redirect Rules',
    EXCLUSION_RULE: 'Exclusion Rules',
    FILE_THREAT_RULE: 'File Threat Rules',
    DLP_RULE: 'File Dlp Rules',
};

export { RULE_REC_TYPE, REV_TYPE };

export const DIFF_TYPE = {
    [RULE_REC_TYPE.CONTENT_RULES]: 'ContentRules',
    [RULE_REC_TYPE.EXCLUSION_RULES]: 'ExclusionRules',
    [RULE_REC_TYPE.REDIRECT_RULES]: 'RedirectRules',
    [RULE_REC_TYPE.FILE_THREAT_RULES]: 'FileThreatRules',
    [RULE_REC_TYPE.DLP_RULES]: 'DlpRules',
    [REV_TYPE.PRIORITY_CHANGED]: 'PriorityChanged',
    [REV_TYPE.BROWSING_ROLE]: 'BrowsingRoles'
};
const getRevLabel = (entityName) => (type) => ({
    [revisionType.add]: `Add ${entityName}`,
    [revisionType.mod]: `Edit ${entityName}`,
    [revisionType.del]: `Delete ${entityName}`,
})[type];

const getSimpleRevLabel = (type) => ({
    [revisionType.add]: 'Add',
    [revisionType.mod]: 'Edit',
    [revisionType.del]: 'Delete',
})[type];

export const RULE_PATH_TYPE_MAP = {
    [ContentRulesStore.PATH]: () => ({
        type: RULE_REC_TYPE.CONTENT_RULES,
        target: 'Content Rule'
    }),
    [RedirectRulesStore.PATH]: () => ({
        type: RULE_REC_TYPE.REDIRECT_RULES,
        target: 'Redirect Rule'
    }),
    [ExclusionRulesStore.PATH]: () => ({
        type: RULE_REC_TYPE.EXCLUSION_RULES,
        target: 'Exclusion Rule'
    }),
    [DlpRulesStore.PATH]: () => ({
        type: RULE_REC_TYPE.DLP_RULES,
        target: 'DLP Rule'
    }),
    [FileThreatRulesStore.PATH]: () => ({
        type: RULE_REC_TYPE.FILE_THREAT_RULES,
        target: 'File Threat Rule'
    })
};
RULE_PATH_TYPE_MAP[ContentRulesStore.PUBLIC_PATH] = RULE_PATH_TYPE_MAP[ContentRulesStore.PATH];
RULE_PATH_TYPE_MAP[RedirectRulesStore.PUBLIC_PATH] = RULE_PATH_TYPE_MAP[RedirectRulesStore.PATH];
RULE_PATH_TYPE_MAP[ExclusionRulesStore.PUBLIC_PATH] = RULE_PATH_TYPE_MAP[ExclusionRulesStore.PATH];
RULE_PATH_TYPE_MAP[DlpRulesStore.PUBLIC_PATH] = RULE_PATH_TYPE_MAP[DlpRulesStore.PATH];
RULE_PATH_TYPE_MAP[FileThreatRulesStore.PUBLIC_PATH] = RULE_PATH_TYPE_MAP[FileThreatRulesStore.PATH];

const getMatchingRulePath = (contextPath) => Object.keys(RULE_PATH_TYPE_MAP)
    .find((path) => contextPath?.startsWith(path));

const makeAccessCodeDisplayData = ({ accessCode }) => {
    const retVal = {
        target: 'Browser Isolation Policy',
        code: accessCode.code,
        snapshot: {
            neverExpire: accessCode.neverExpire,
            expirationDate: accessCode.neverExpire ? 'Never Expire' : accessCode.expirationDate,
            unlimitedActivations: accessCode.unlimitedActivations,
            activationsAllowed: accessCode.unlimitedActivations ? 'Unlimited' : accessCode.activationsAllowed,
        }
    };
    if (accessCode.browsingRoleName) {
        retVal.target = 'Policies Access Codes';
        retVal.browsingRoleName = accessCode.browsingRoleName;
        retVal.snapshot.expirationDate = accessCode.neverExpire ?
            'Never Expire' : getGmtDateString(accessCode.expirationDate);
    } else {
        retVal.policyName = accessCode.policyName;
    }
    return retVal;
};

const customEventDisplayData = {
    ADMIN_USER_CREATED: ({ userEmail }) => ({
        action: 'Add Admin',
        target: userEmail,
    }),
    ADMIN_USER_UPDATED: ({ userEmail }) => ({
        action: 'Update Admin',
        target: userEmail,
    }),
    ADMIN_USER_DELETED: ({ userEmail }) => ({
        action: 'Delete Admin',
        target: userEmail,
    }),
    ADMIN_LOG_IN: () => ({
        action: 'Admin logged in',
    }),
    ADMIN_LOG_OUT: () => ({
        action: 'Admin logged out',
    }),
    SSO_ADMIN_USER_CREATED: ({ userEmail }) => ({
        action: 'SAML/SSO Admin Created',
        target: userEmail,
    }),
    SSO_ADMIN_USER_UPDATED: ({ userEmail }) => ({
        action: 'SAML/SSO Admin Changed',
        target: userEmail,
    }),
    SSO_ADMIN_USER_DELETED: ({ userEmail }) => ({
        action: 'SAML/SSO Admin Deleted',
        target: userEmail,
    }),
    SSO_ADMIN_LOG_IN: () => ({
        action: 'SAML/SSO Admin logged in',
    }),
    SSO_ADMIN_LOG_OUT: () => ({
        action: 'SAML/SSO Admin logged out',
    }),
    MAIL_ADMIN_WELCOME: ({ to }) => ({
        action: 'Welcome email sent',
        target: to,
    }),
    EMAIL_ADMIN_WELCOME_PRIMARY: ({ to }) => ({
        action: 'Welcome email sent',
        target: to,
    }),
    MAIL_ADMIN_PASSWORD_SETUP: ({ to }) => ({
        action: 'Password reset email sent',
        target: to,
    }),
    CREATE_ACCESS_CODE: (metadata) => ({
        action: `${metadata.accessCode.browsingRoleName ? '' : 'Access Code'} Created`,
        ...makeAccessCodeDisplayData(metadata),
    }),
    EDIT_ACCESS_CODE: (metadata) => ({
        action: `${metadata.accessCode.browsingRoleName ? '' : 'Access Code'} Edited`,
        ...makeAccessCodeDisplayData(metadata),
    }),
    DELETE_ACCESS_CODE: (metadata) => ({
        action: `${metadata.accessCode.browsingRoleName ? '' : 'Access Code'} Deleted`,
        ...makeAccessCodeDisplayData(metadata),
    }),
    MALICIOUS_THREAT: () => ({
        action: 'Edit Custom Block Page',
        target: 'Malicious Threat',
    }),
    CONTENT_FILTERING: () => ({
        action: 'Edit Custom Block Page',
        target: 'Content Filtering',
    }),
    REGISTRATION_NOTICE: () => ({
        action: 'Edit Registration Notice',
        target: 'Registration notice',
    }),
    IDP_CONFIGURATION_CHANGED: () => ({
        action: 'IdP Settings Changed',
        target: 'SAML/SSO',
    }),
    CONVERT_POLICIES_TO_RULES: () => ({
        action: 'Old Policies Converted',
    }),
    /* @param { type: PRIORITY_CHANGE_TYPES } */
    [REV_TYPE.PRIORITY_CHANGED]: ({ type }) => ({
        action: 'Priority Changed',
        target: type,
    }),
    [REV_TYPE.USER_GROUP_SOURCE_CHANGED]: () => ({
        action: 'Edit User Group Source',
        target: 'Role - User Group Source',
    }),
};

const parse = (str) => {
    try {
        return JSON.parse(str);
    } catch (e) {
        debug.recordError(e);
        return {};
    }
};

export default class AuditStore {
    static PropType = PropTypes.shape({
        load: PropTypes.func.isRequired,
        loadStatus: AsyncActionStore.PropType.isRequired,
        search: InputStore.PropType.isRequired,
        paginator: PaginationStore.PropType.isRequired,
        modal: MenuStore.PropType.isRequired,
        filteredList: MobxPropTypes.arrayOrObservableArray,
        categoryNameById: MobxPropTypes.objectOrObservableObject,
        highRiskCategoryIds: MobxPropTypes.arrayOrObservableArray, // Must remain for backwards compat.
        getSelectedRecord: PropTypes.func.isRequired,
        getDiff: PropTypes.func.isRequired,
        showPrev: PropTypes.func.isRequired,
        showNext: PropTypes.func.isRequired,
        inputStores: PropTypes.objectOf(InputStore.PropType),
        getInputStore: PropTypes.func.isRequired,
        filterData: PropTypes.func.isRequired,
        searchParamsToUrl: PropTypes.func.isRequired,
        urlToSearchParams: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        // store original revision in "non-observable" object
        this.revisionById = {};

        extendObservable(this, /** @class AuditStore */{
            apiGetAll: apiCall('/console/audit/'),
            apiGetPolicy: apiCall('/console/audit/details/CustomerPolicy'),
            apiGetFullRevision: (type) => apiCall(`/console/audit/details/fullRevision/${type}`),
            loadStatus: new AsyncActionStore(),
            detailsStatus: new AsyncActionStore(),
            search: new InputStore(),
            paginator: new PaginationStore(),
            list: [],
            filteredList: [],
            modal: new MenuStore(),
            categoryNameById: {},
            highRiskCategoryIds: [],

            forbiddenCategoriesFilterStore: new InputStore(),
            whitelistRulesFilterStore: new InputStore(),
            blacklistRulesFilterStore: new InputStore(),
            userGroupsFilterStore: new InputStore(),
            isolatedCategoriesFilterStore: new InputStore(),
            whiteListFilterStore: new InputStore(),
            ipAddressFilterStore: new InputStore(),

            inputStores: {},
        }, data);

        reaction(() => this.search.value, this.setFilteredList);
        reaction(() => this.modal.id, () => {
            this.forbiddenCategoriesFilterStore.setValue('');
            this.whitelistRulesFilterStore.setValue('');
            this.blacklistRulesFilterStore.setValue('');
            this.userGroupsFilterStore.setValue('');
            this.isolatedCategoriesFilterStore.setValue('');
            this.whiteListFilterStore.setValue('');
            this.ipAddressFilterStore.setValue('');
        });
        reaction(() => this.paginator.pageSizeSelect.value, () => this.paginator.onPage(0));

        reaction(() => this.modal.id, () => {
            const record = this.getSelectedRecord();
            const isRule = record && Object.values(RULE_REC_TYPE).includes(record.type);
            const needsPartialLoading = record && [
                'customerPolicy',
                'registrationOptions',
                REV_TYPE.BROWSING_ROLE
            ].includes(record.type) || isRule;

            if (needsPartialLoading) {
                const revision = this.revisionById[record.id];
                const previousRevision = this.getPreviousRevision(record, revision, 'target.id');
                const revisions = [revision, previousRevision].filter((r) => r && !this.isPolicyRevisionLoaded(r));
                // load revisions that are not loaded
                if (revisions.length) {
                    const apiGetFullDataFn = ['customerPolicy', 'registrationOptions'].includes(record.type) ?
                        this.apiGetPolicy :
                        this.apiGetFullRevision(record.type);

                    this.detailsStatus.setLoading(apiGetFullDataFn(revisions.map((r) => r.revisionId))
                        .then(action((fullRevisions) => {
                            fullRevisions.forEach((fullRev) => revisions.forEach((partialRev) => {
                                if (fullRev.revisionId === partialRev.revisionId
                                    && fullRev.target.id === partialRev.target.id) {
                                    const rec = this.list.find((item) => item.id === partialRev.id);
                                    // Old policy only
                                    if (rec && fullRev.target.policy) {
                                        rec.policyName = fullRev.target.policy.name;
                                    } else {
                                        rec.name = fullRev.target.name;
                                    }
                                    if (rec.type === RULE_REC_TYPE.CONTENT_RULES) {
                                        fullRev.target.exitOption =
                                            PageRestrictionsStore.valuesToExitOption(fullRev.target);
                                    }
                                    partialRev.target = fullRev.target;
                                    // Revision type agnostic way to determine whether info has been loaded
                                    partialRev.expanded = true;
                                    // Old policy only
                                    if (partialRev.target.policy) {
                                        partialRev.target.policy.priority = partialRev.target.priority;
                                    }
                                }
                            }));
                        })));
                    return;
                }
            }

            this.detailsStatus.setBase();
        });
    }

    searchParamsToUrl = (obj) =>
        encodeURIComponent(JSON.stringify(obj))

    // Method is intended to be used with props.location.query, which decodes the string for us
    urlToSearchParams =
        JSON.parse.bind(JSON)


    filterData = (list, { queries }) =>
        list.filter(this.doesListEntryMatchSomeQuery(queries || []))
            .sort(this.timestampSorter);

    doesListEntryMatchSomeQuery = (queries) => (entry) =>
        queries.some(this.isQuerySatisfied(entry));

    isQuerySatisfied = (entry) => (searchCriteria) =>
        Object.entries(searchCriteria)
            .map((criteria) => {
                const entryVal = entry[criteria[0]];
                const criteriaVal = criteria[1];
                return [entryVal, criteriaVal];
            })
            .every(this.valueMatchesCriteria);

    valueMatchesCriteria = ([value, criteria]) =>
        value === criteria

    timestampSorter = (a, b) =>
        (a.ts > b.ts ? -1 : 1)

    getInputStore = (key) => {
        if (!this.inputStores[key]) {
            this.inputStores[key] = new InputStore();
        }
        return this.inputStores[key];
    }

    getActor = (revision) =>
        revision.actorEmail || (revision.contextPath?.startsWith('/api/v3') ? 'Policy API' : '')

    isPolicyRevisionLoaded = (p) => p && p.target && (
        // Old mechanism
        (typeof p.target.policy !== 'string' && ['customerPolicy', 'registrationOptions'].includes(p.type)) ||
        // New mechanism. Can probably replace the old one but would be best to test it out explicitly
        !!p.expanded
    )

    convertServerDataToNiceList = (data) => {
        let list = [];
        const idGen = idMaker();
        this.revisionById = {};

        const addRevision = (fn) => (revision) => {
            const id = idGen();
            const res = fn(revision);
            res.revisionId = revision.revisionId;
            res.id = id;
            revision.id = id;
            this.revisionById[id] = revision;
            return res;
        };

        list = list.concat(data.customAuditEvents.map((event) => {
            event.metadata = event.metadata ? parse(event.metadata) : {};
            const customEvent = customEventDisplayData[event.eventName];
            if (customEvent && typeof customEvent === 'function') {
                const displayData = customEvent(event.metadata);
                if (displayData) {
                    const ts = +moment(event.eventTime);
                    return addRevision(() => ({
                        ...displayData,
                        type: event.eventName,
                        ts,
                        actor: event.actorEmail || '',
                    }))({ ...event, ts });
                }
            }
            return null;
        }).filter(Boolean));

        const isUrlIsolationPolicy = (policy) => (policy.includes && policy.includes('URLIsolationPolicy'))
            || policy.type === 'URLIsolationPolicy';
        list = list.concat(data.customerPolicy.filter((revision) => !(revision.contextPath
            && revision.contextPath.startsWith('/console/registration_options')))
            .map((revision) => {
                if (revision.contextPath
                    && revision.contextPath.includes('url_isolation_policy')
                    && this.isPolicyRevisionLoaded(revision)) {
                    revision.target.policy.priority = revision.target.priority;
                }
                return revision;
            }).map(addRevision((revision) => ({
                type: 'customerPolicy',
                ts: revision.ts,
                action: customerPolicyLabelByRevType[revision.revisionType],
                target: isUrlIsolationPolicy(revision.target.policy)
                    ? 'URL Isolation Policy' : 'Browser Isolation Policy',
                actor: revision.actorEmail || '',
                policyName: typeof revision.target.policy === 'string' ? undefined : revision.target.policy.name,
            }))));

        list = list.concat(data.apiKey.map(addRevision((revision) => ({
            type: 'apiKey',
            ts: revision.ts,
            action: apiKeyLabelByRevType[revision.revisionType],
            target: 'Reporting API',
            actor: revision.actorEmail || '',
            apiKey: revision.target.value,
        }))));

        list = list.concat((data.customerCategory || [])
            .filter((item) => item.target.category.type === 'CustomCategory')
            .map(addRevision((revision) => ({
                type: 'CustomerCategory',
                ts: revision.ts,
                action: getRevLabel('Custom Category')(revision.revisionType),
                target: 'Custom Categories',
                actor: this.getActor(revision),
                category: revision.target.category,
                hidden: !revision.contextPath
                    ?.match(`^${CustomCategoriesStore.PATH}|${CustomCategoriesStore.PUBLIC_PATH}`),
            }))));

        list = list.concat((data.rules || [])
            .filter((item) => item.contextPath === '/super_api/customer/redesigned_policies/migrate')
            .map(addRevision((revision) => ({
                type: revision.target.type,
                ts: revision.ts,
                hidden: true,
            }))));

        // reg options
        // group by revision id
        const regOptRevision = {};
        data.customerPolicy.filter((revision) => revision.contextPath
            && revision.contextPath.startsWith('/console/registration_options'))
            .forEach((revision) => {
                regOptRevision[revision.revisionId] = regOptRevision[revision.revisionId] || {};
                const rev = regOptRevision[revision.revisionId];
                rev.revisionId = revision.revisionId;
                rev.ts = revision.ts;
                rev.actorEmail = revision.actorEmail;
                rev.target = rev.target || {};
                rev.target.id = revision.target.id;
                rev.target.policy = revision.target.policy;
                rev.target.type = 'Email registration option changed';
                rev.whitelist = [];
                rev.ipRenge = {};
            });

        data.whitelist.filter((revision) => revision.contextPath
            && revision.contextPath.startsWith('/console/registration_options'))
            .forEach((revision) => {
                const rev = regOptRevision[revision.revisionId];
                if (rev && revision.revisionType === revisionType.add) {
                    rev.whitelist.push(revision.target.emailDomain);
                }
            });

        const ipRange = data.ipRange || [];
        ipRange.forEach((revision) => {
            regOptRevision[revision.revisionId] = regOptRevision[revision.revisionId] || {};
            const rev = regOptRevision[revision.revisionId];
            rev.ts = revision.ts;
            rev.actorEmail = revision.actorEmail;
            rev.target = rev.target || {};
            rev.target.id = 'ipRange';
            rev.ipRenge = rev.ipRenge || {};
            rev.ipRenge.ipAddress = rev.ipRenge.ipAddress || [];
            rev.target.type = 'IP range registration option changed';
            if (revision.revisionType === revisionType.add) {
                rev.ipRenge.ipAddress.push(revision.target.ipAddress);
            }
        });

        list = list.concat(Object.values(regOptRevision).map(addRevision((revision) => ({
            type: 'registrationOptions',
            ts: revision.ts,
            action: revision.target.type,
            target: 'Registration options',
            actor: revision.actorEmail || '',
            apiKey: revision.target.value,
        }))));

        list = list.concat(data.metaConfig.map(addRevision((revision) => ({
            type: 'metaConfig',
            ts: revision.ts,
            action: 'General Settings Changed',
            target: 'SAML/SSO ',
            actor: revision.actorEmail || '',
        }))));

        const authorityMap = {};
        const revisionMap = {};
        list = list.concat(data.metaAuthority
            .filter((revision) => {
                authorityMap[revision.target.id] = revision.target.authority;
                if (revisionMap[revision.revisionId]) {
                    revisionMap[revision.revisionId].authorityIds.push(revision.target.id);
                    return false;
                }
                revision.authorityIds = [revision.target.id];
                revisionMap[revision.revisionId] = revision;
                return true;
            })
            .map(addRevision((revision) => {
                const groupAudits = data.metaGroup.filter((groupAudit) =>
                    groupAudit.revisionId === revision.revisionId &&
                    revision.authorityIds.includes(groupAudit.target.authorityId));

                const authoritiesToGroups = {};
                groupAudits
                    .filter((groupAudit) => groupAudits.every((otherGroupAudit) =>
                        otherGroupAudit.target.id === groupAudit.target.id ||
                        otherGroupAudit.target.nativeId !== groupAudit.target.nativeId ||
                        authorityMap[otherGroupAudit.target.authorityId] !==
                        authorityMap[groupAudit.target.authorityId]))
                    .forEach((groupAudit) => {
                        const authority = authorityMap[groupAudit.target.authorityId];
                        authoritiesToGroups[authority] = authoritiesToGroups[authority] || { added: [], removed: [] };
                        if (groupAudit.revisionType === revisionType.add) {
                            authoritiesToGroups[authority].added.push(groupAudit.target.name);
                        } else if (groupAudit.revisionType === revisionType.del) {
                            authoritiesToGroups[authority].removed.push(groupAudit.target.name);
                        }
                    });

                const roleNames = Object.keys(authoritiesToGroups).map((authority) => fetchDisplayRoleName(authority));
                const targetName = roleNames.join('/') + ' Role' + (roleNames.length === 1 ? '' : 's');
                return {
                    type: 'metaAuthority',
                    ts: revision.ts,
                    action: 'Edit Admin SAML/SSO Role Access',
                    actor: revision.actorEmail || '',
                    target: targetName,
                    authoritiesToGroups,
                };
            })));

        const filterCustomerByContextAndField = (context, field) => data.customer
            .filter((r) => (r.contextPath || '').startsWith(context))
            .reduce((acc, r) => {
                if (acc.length === 0 || acc[acc.length - 1].target[field] !== r.target[field]) {
                    acc.push(r);
                }
                return acc;
            }, [])
            .map((r) => {
                if (r.target[field]) {
                    r.target[field] = parse(r.target[field]);
                }
                return r;
            });

        list = list.concat(filterCustomerByContextAndField('/console/browser_bar/update', 'browserBarOptions')
            .map(addRevision((revision) => ({
                type: 'browserBar',
                ts: revision.ts,
                action: 'Customize UI',
                target: 'Browser Bar',
                actor: revision.actorEmail || '',
            }))));

        list = list.concat(filterCustomerByContextAndField('/console/exit_message/update', 'customExitMessage')
            .map(addRevision((revision) => ({
                type: 'exitMessage',
                ts: revision.ts,
                action: 'Customize UI',
                target: 'Exit Message',
                actor: revision.actorEmail || '',
            }))));


        list = list.concat(data.customer
            .filter((r) => (r.contextPath || '').startsWith('/console/policy-migration/update'))
            .map(addRevision((revision) => ({
                type: 'policyMigrationStep',
                ts: revision.ts,
                action: 'Edit New Policies Migration',
                target: 'New Policies Migration',
                actor: revision.actorEmail || '',
            }))));

        list = list.concat((data.rules || [])
            .reduce((acc, ruleRevision) => {
                const rulePath = getMatchingRulePath(ruleRevision.contextPath);

                if (!rulePath) {
                    return acc;
                }
                return [
                    ...acc,
                    addRevision((r) => ({
                        ts: r.ts,
                        actor: this.getActor(r),
                        action: getSimpleRevLabel(r.revisionType),
                        revisionId: r.revisionId,
                        ...RULE_PATH_TYPE_MAP[rulePath](r)
                    }))(ruleRevision)
                ];
            }, [])
        );

        list = list.concat((data.browsingRoles || [])
            .filter((r) => r.contextPath?.match(`^${BrowsingRolesStore.PATH}|${BrowsingRolesStore.PUBLIC_PATH}`))
            .map(addRevision((r) => ({
                ts: r.ts,
                revisionId: r.revisionId,
                actor: this.getActor(r),
                action: getSimpleRevLabel(r.revisionType),
                target: 'Browsing Role',
                type: REV_TYPE.BROWSING_ROLE,
            }))));


        // preformat dates so we can search through them
        list.forEach((item) => {
            item.time = moment(item.ts).format('MM/DD/YYYY, hh:mm:ss A');
        });

        return list.sort((a, b) => b.ts - a.ts);
    };

    load = action(() => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiGetAll().then(action((data) => {
                this.list.replace(this.convertServerDataToNiceList(data));
                data.categories.forEach(({ id, name }) => {
                    this.categoryNameById[id] = name;
                });
                this.highRiskCategoryIds.replace(data.highRiskCategoryIds);
                this.setFilteredList();
                return data;
            })));
        }
        return this.loadStatus.promise;
    });

    setFilteredList = action(() => {
        const filter = this.search.value.toLowerCase();
        this.filteredList.replace((
            filter ?
                this.list.filter((event) => [event.action, event.target, event.time, event.actor]
                    .map((s) => (s || '').toLocaleLowerCase())
                    .join(' ')
                    .indexOf(filter) !== -1) :
                this.list
        ).filter((e) => !e.hidden));

        this.paginator.setItemTotalCount(this.filteredList.length);
        this.paginator.onPage(0);
    });

    getSelectedRecord = () => this.list.find((item) => item.id === this.modal.id);

    showPrev = action((auditRecord) => {
        const prevIndex = Math.max(0, this.filteredList.indexOf(auditRecord) - 1);
        this.modal.open(this.filteredList[prevIndex].id);
        if ((prevIndex + 1) % this.paginator.pageSizeSelect.value === 0) {
            this.paginator.onPage(this.paginator.pageIndex - 1);
        }
    });

    showNext = action((auditRecord) => {
        const nextIndex = Math.min(this.filteredList.length - 1, this.filteredList.indexOf(auditRecord) + 1);
        this.modal.open(this.filteredList[nextIndex].id);
        if (nextIndex % this.paginator.pageSizeSelect.value === 0) {
            this.paginator.onPage(this.paginator.pageIndex + 1);
        }
    });

    getPreviousRecord = (record, filterFn) => {
        return this.list.find((r) => {
            if (r.ts > record.ts) {
                return false;
            }
            if (r.id === record.id) {
                return false;
            }
            return filterFn(r);
        });
    };

    renameCategories = (arrDiff) => {
        if (!arrDiff) {
            return;
        }
        const getCategoryName = (categoryId) => this.categoryNameById[categoryId] || categoryId;
        arrDiff.added = arrDiff.added.map(getCategoryName);
        arrDiff.removed = arrDiff.removed.map(getCategoryName);
    };

    patchHighRiskCategories = (diff, isNew = false) => {
        const categoriesChanged = diff.isolatedCategories !== undefined;
        const highRiskChanged = diff.isolateHighRisk !== undefined;

        if (!diff || (!categoriesChanged && !highRiskChanged)) {
            return;
        }

        if (!categoriesChanged && highRiskChanged) {
            diff.isolatedCategories = {
                added: [],
                removed: [],
            };
        }

        if (highRiskChanged
            && !(isNew && !diff.isolateHighRisk) // render nothing when policy is added with high risk unselected
        ) {
            diff.isolatedCategories[diff.isolateHighRisk ? 'added' : 'removed'].unshift('High Risk Sites');
        }
    };

    getPreviousRevision = (record, revision, path) => {
        const targetId = _get(revision, path);

        const previousRecord = this.getPreviousRecord(record, (r) => {
            if (r.type !== record.type) {
                return false;
            }
            return targetId !== undefined && targetId === _get(this.revisionById[r.id], path);
        });
        return previousRecord && this.revisionById[previousRecord.id];
    };

    dependantPropsLinker = (target, diff) => {
        const getTotalDiff = once(() => objectDiff(target, {}));

        return (predicate, dependantProps) => {
            // Do not show Dependant props if they are unused
            // Example: do not show audit browsing roles if "match all browsing roles" is true
            if (predicate(target)) {
                dependantProps.forEach((p) => {
                    delete diff[p];
                });
            }
            // Show all dependant props if their state changes from unused to used
            // Example:
            // Edit 1: "match all browsing roles" is set to true
            // Edit 2: "match all browsing roles" is set to false
            // The last audit log should not just show "match all" set to false,
            // but also all active browsing roles
            if (!predicate(diff)) {
                dependantProps.forEach((p) => {
                    diff[p] = getTotalDiff()[p];
                });
            }
        };
    }

    linkRuleProps = (target, diff) => {
        const depPropsLinker = this.dependantPropsLinker(target, diff);

        depPropsLinker((o) => o.matchAllCategories, ['standardCategories.items', 'customerCategories']);
        depPropsLinker((o) => o.matchAllRoles, ['browsingRoles']);

        return depPropsLinker;
    }

    linkFileRuleProps = (target, diff) => {
        const linkDependantProps = this.linkRuleProps(target, diff);

        linkDependantProps((o) => o.matchAllCountries, ['matchCountries.items']);
        linkDependantProps((o) => o.matchAllFileTypes, ['matchFileTypes.items']);
        linkDependantProps((o) => o.matchAllFileSize, ['maximumSizeAllowed']);
        linkDependantProps((o) => o.action !== 'SCAN', [
            'blockUnsupportedFiles',
            'blockOnTimeout',
        ]);
        delete diff.matchUploads;
        delete diff.matchDownloads;
        diff._virtual_uploadsDownloads = {
            ud: 'Uploads and Downloads',
            u: 'Uploads',
            d: 'Downloads',
        }[(target.matchUploads ? 'u' : '') + (target.matchDownloads ? 'd' : '')];
        return linkDependantProps;
    }

    /** WARNING: Operates on the diff directly */
    postProcessDiff = (t, d) => {
        const processors = {
            [DIFF_TYPE[RULE_REC_TYPE.DLP_RULES]]: (target, diff) => {
                const linkDepProps = this.linkFileRuleProps(target, diff);
                linkDepProps((o) => o.action !== 'SCAN', ['blockOnDetection', 'detectorSets.items']);
            },
            [DIFF_TYPE[RULE_REC_TYPE.FILE_THREAT_RULES]]: (target, diff) => {
                const linkDepProps = this.linkFileRuleProps(target, diff);
                linkDepProps((o) => o.action !== 'SCAN', ['blockMalware']);
            }
        };
        processors[d.type]?.(t, d);
    }

    // TODO: streamline this next time big changes are made (more than 2 entity type diffs)
    getDiff = (record) => {
        const revision = this.revisionById[record.id];
        if (record.type === 'customerPolicy') {
            if (revision.revisionType === revisionType.del) {
                return null;
            }
            if (revision && !this.isPolicyRevisionLoaded(revision)) {
                return null;
            }
            const previousRevision = this.getPreviousRevision(record, revision, 'target.id');
            if (previousRevision && !this.isPolicyRevisionLoaded(previousRevision)) {
                return null;
            }
            const diff = objectDiff(revision ? revision.target.policy : {},
                previousRevision ? previousRevision.target.policy : {});

            if (diff && Object.keys(diff).length > 0) {
                this.patchHighRiskCategories(diff, revision.revisionType === revisionType.add);
                this.renameCategories(diff.isolatedCategories);
                this.renameCategories(diff['contentFiltering.forbiddenCategories']);

                diff.type = revision.target.policy.type;
            } else {
                return null;
            }

            return diff;
        }

        if (record.type === 'metaAuthority') {
            return {
                type: 'MetaAuthority',
                fullAccessUserGroups: record.authoritiesToGroups[ROLE_FULL_ACCESS.value],
                readOnlyUserGroups: record.authoritiesToGroups[ROLE_READ_ONLY.value],
            };
        }

        const checkType = (type) => type === 'ADMIN_USER_CREATED' ||
            type === 'SSO_ADMIN_USER_CREATED' || type === 'ADMIN_USER_UPDATED' || type === 'SSO_ADMIN_USER_UPDATED';
        if (checkType(record.type)) {
            const targetId = _get(revision, 'metadata.userId');
            const previousRecord = this.getPreviousRecord(record, (r) => {
                if (!checkType(r.type)) {
                    return false;
                }
                return targetId !== undefined && targetId === _get(this.revisionById[r.id], 'metadata.userId');
            });
            const previousRevision = previousRecord && this.revisionById[previousRecord.id];

            const getDataToDiff = (rev) =>
                (rev ? _pick(rev.metadata, ['userEmail', 'firstName', 'lastName', 'userAuthorities']) : {});
            const diff = objectDiff(getDataToDiff(revision), getDataToDiff(previousRevision));

            if (Object.keys(diff).length > 0) {
                diff.type = 'User';
                if (diff.userAuthorities) {
                    const isAddedReadOnly =
                        diff.userAuthorities.added.find((item) => item.authority === ROLE_READ_ONLY.value) &&
                        diff.userAuthorities.removed.every((item) => item.authority !== ROLE_READ_ONLY.value);
                    const isAddedFullAccess =
                        diff.userAuthorities.added.find((item) => item.authority === ROLE_FULL_ACCESS.value) &&
                        diff.userAuthorities.removed.every((item) => item.authority !== ROLE_FULL_ACCESS.value);

                    if (isAddedReadOnly) {
                        diff.roleAccess = 'Read Only';
                    } else if (isAddedFullAccess) {
                        diff.roleAccess = 'Full Access';
                    }
                }
            } else {
                return null;
            }

            return diff;
        }

        if (record.type === 'MALICIOUS_THREAT' ||
            record.type === 'CONTENT_FILTERING' ||
            record.type === 'REGISTRATION_NOTICE'
        ) {
            const previousRevision = this.getPreviousRevision(record, revision, 'eventName');
            const diff = objectDiff(
                revision ? { ...revision.metadata.content, enabled: revision.metadata.enabled } : {},
                previousRevision ? {
                    ...previousRevision.metadata.content,
                    enabled: previousRevision.metadata.enabled,
                } : {}, {}, '', ['content']);

            if (diff && Object.keys(diff).length > 0) {
                if (record.type !== 'REGISTRATION_NOTICE') {
                    diff.type = 'CustomBlockPage';
                    if (revision.metadata.content.logo === 'equals') {
                        delete diff.logo;
                    } else if (revision.metadata.content.logo === 'removed') {
                        diff.logo = 'removed';
                    } else {
                        diff.logo = 'changed';
                    }
                } else {
                    diff.type = 'RegistrationNotice';
                }

                if (revision?.metadata?.content?.content) { // indicates new format
                    // when saving new format there is no need to show that old fields were removed
                    delete diff.message;
                    delete diff.logo;
                    delete diff.headline;
                    delete diff.headlineIcon;
                    delete diff.background;
                    delete diff.headlineIcon;
                }
            } else {
                return null;
            }

            return diff;
        }

        if (accessCodeActions.indexOf(record.type) !== -1) {
            const previousRecord = this.getPreviousRecord(record, (r) => {
                return record.code !== undefined
                    && record.code === _get(this.revisionById[r.id], 'metadata.accessCode.code');
            });

            const diff = objectDiff(record ? record.snapshot : {},
                previousRecord ? previousRecord.snapshot : {});

            if (Object.keys(diff).length > 0) {
                diff.type = 'AccessCodes';
                diff.code = diff.code || revision.metadata.code;
                if (record.browsingRoleName) {
                    diff.type = 'AccessCodesRedesigned';
                }
            } else {
                return null;
            }

            return diff;
        }

        if (record.type === 'registrationOptions') {
            if (revision && !this.isPolicyRevisionLoaded(revision)) {
                return null;
            }
            const previousRevision = this.getPreviousRevision(record, revision, 'target.id');
            if (previousRevision && !this.isPolicyRevisionLoaded(previousRevision)) {
                return null;
            }

            const getDataToDiff = (rev) => {
                const retVal = {};
                if (rev && rev.target.policy) {
                    let emailRegistration = rev.target.policy.useAccessCodes ?
                        'Activation Codes' : 'Activation Links';
                    emailRegistration = rev.target.policy.useSSoLogin ? 'SAML/SSO' : emailRegistration;
                    retVal.emailRegistration = rev.target.policy.emailRegistrationDisabled ?
                        'Disabled' : emailRegistration;
                    retVal.whitelist = rev.whitelist || [];
                }
                if (rev && Object.keys(rev.ipRenge).length > 0) {
                    retVal.ipAddress = rev.ipRenge.ipAddress || [];
                }
                return retVal;
            };

            const diff = objectDiff(revision ? getDataToDiff(revision) : {},
                previousRevision ? getDataToDiff(previousRevision) : {});

            if (diff && Object.keys(diff).length > 0) {
                diff.type = 'RegistrationOptions';
            } else {
                return null;
            }

            return diff;
        }

        if (record.type === 'metaConfig') {
            const previousRevision = this.getPreviousRevision(record, revision, 'target.customerId');
            const diff = objectDiff(revision ? revision.target : {},
                previousRevision ? previousRevision.target : {});

            if (Object.keys(diff).length > 0) {
                diff.type = 'MetaConfig';
            } else {
                return null;
            }

            return diff;
        }

        if (record.type === 'IDP_CONFIGURATION_CHANGED') {
            return Object.assign({}, revision.metadata, {
                type: 'MetaConfig',
            });
        }

        if (record.type === REV_TYPE.USER_GROUP_SOURCE_CHANGED) {
            return Object.assign({}, revision.metadata);
        }

        if (record.type === 'browserBar') {
            const previousRevision = this.getPreviousRevision(record, revision, 'target.customerId');
            const currentValue = revision?.target.browserBarOptions ?? getDefaultBrowserBarOptions();
            const prevValue = previousRevision?.target.browserBarOptions ?? getDefaultBrowserBarOptions();
            const diff = objectDiff(currentValue, prevValue, {}, '', ['popupContent']);
            diff.type = 'BrowserBar';
            // hide old format change when switching to new one
            if (currentValue.popupContent) {
                delete diff.popupMessage;
            }
            return diff;
        }

        if (record.type === 'exitMessage') {
            const getDefaultCustomExitMessage = () => ({ option: 'DEFAULT', text: '' });
            const previousRevision = this.getPreviousRevision(record, revision, 'target.customerId');
            const currentValue = revision?.target.customExitMessage ?? getDefaultCustomExitMessage();
            const prevValue = previousRevision?.target.customExitMessage ?? getDefaultCustomExitMessage();
            const diff = objectDiff(currentValue, prevValue, {}, '', ['content']);
            diff.type = 'ExitMessage';
            // hide old format change when switching to new one
            if (currentValue.content) {
                delete diff.text;
            }
            // always show recent content when setting is enabled
            if (diff.option === 'CUSTOM') {
                if (currentValue.content) {
                    diff.content = currentValue.content;
                } else {
                    diff.text = currentValue.text;
                }
            }
            return diff;
        }

        if (record.type === 'policyMigrationStep') {
            const previousRevision = this.getPreviousRevision(record, revision, 'target.customerId');
            const diff = objectDiff(revision ? revision.target : {}, previousRevision ? previousRevision.target : {});
            diff.type = 'PolicyMigration';
            return diff;
        }

        if (record.type === REV_TYPE.PRIORITY_CHANGED) {
            const { names, newOrder, oldOrder } = revision.metadata;
            const idToName = (id) => names[id];
            return {
                newOrder: newOrder.reverse().map(idToName),
                type: DIFF_TYPE[REV_TYPE.PRIORITY_CHANGED],
                oldOrder: oldOrder.reverse().map(idToName),
            };
        }

        if (record.type !== 'CustomerCategory' && [
            ...Object.values(RULE_REC_TYPE),
            REV_TYPE.BROWSING_ROLE
        ].includes(record.type)) {
            if (revision.revisionType === revisionType.del) {
                return null;
            }
            if (revision && !this.isPolicyRevisionLoaded(revision)) {
                return null;
            }
            // Using an object is done for storybook only. In the real world there would be no previous revision even
            // if we try fetching it.
            const previousRevision = revision.revisionType === revisionType.add ? {} :
                this.getPreviousRevision(record, revision, 'target.id');

            const diff = objectDiff(revision ? revision.target : {},
                previousRevision ? previousRevision.target : {});

            diff.type = DIFF_TYPE[record.type];
            diff.previousRevision = previousRevision;
            this.postProcessDiff(revision.target, diff);
            return diff;
        }

        if (record.type === 'CustomerCategory') {
            if (revision.revisionType === revisionType.del) {
                return null;
            }

            const previousRevision = this.getPreviousRevision(record, revision, 'target.id');
            const diff = objectDiff(revision ? revision.target.category : {},
                previousRevision ? previousRevision.target.category : {});

            if (Object.keys(diff).length > 0) {
                diff.type = 'CustomerCategory';
                return diff;
            } else {
                return null;
            }
        }

        return null;
    };
}
